import React, { useEffect, useMemo, useState } from 'react';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ErrorHandler, Modal, PageSteps } from '@common/components';
import { SuppliersManagementLocationsUpdateStepReview } from './_suppliers-management-locations-update-step-review';
import { SuppliersManagementLocationsUpdateStepActiveItems } from './_suppliers-management-locations-update-step-active-items';
import {
  ORGANIZATION_UNITS_QUERY,
  SUPPLIER_QUERY,
} from '@institutions/graphql';
import {
  useMutation,
  useQuery,
  useNavigate,
  useLocation,
  useParams,
} from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  UpdateSupplierDepartmentsMutation,
  UpdateSupplierDepartmentsResponse,
  UpdateSupplierDepartmentsVariables,
} from '@institutions/graphql/mutations/update-supplier-departments';
import { SuppliersManagementRolesAccessSelectionStep } from './_suppliers-management-locations-update-step-roles-access';
import {
  TieringRadioWithOptions,
  TieringRadioTierType,
  UnitSelectionOnTransferTables,
} from '@institutions/components';

export type Answers = {
  rejectSubmissions?: boolean;
  retractOffers?: boolean;
  retractExtensionOffers?: boolean;
};

const hasAnswer = (value?: boolean) => typeof value === 'boolean';

export function SuppliersManagementLocationsUpdateSteps() {
  const { pathname } = useLocation();
  const currentMembership = useCurrentMembership();

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.SUPPLIERS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE,
    },
    {
      name: pathname.includes('activation')
        ? 'Activate Supplier'
        : 'Locations Update',
      path: pathname,
    },
  ]);

  const navigate = useNavigate();
  const { supplierId = '' } = useParams<{
    supplierId: string;
  }>();

  const [selectedOrganizationUnitsIds, setSelectedOrganizationUnitsIds] =
    useState<string[]>([]);
  const [removedOrganizationUnitsIds, setRemovedOrganizationUnitsIds] =
    useState<string[]>([]);
  const [showActiveItemsStep, setShowActiveItemsStep] = useState(false);
  const [selectedTierOption, setSelectedTierOption] =
    useState<TieringRadioTierType>({ id: '' });
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Answers>({});
  const [notes, setNotes] = useState('');

  const [updateSupplierDepartmentsMutation, { loading: mutationLoading }] =
    useMutation<
      UpdateSupplierDepartmentsResponse,
      UpdateSupplierDepartmentsVariables
    >(UpdateSupplierDepartmentsMutation);

  const {
    data: unitsData,
    error: unitsError,
    loading: unitsLoading,
  } = useQuery(ORGANIZATION_UNITS_QUERY, {
    variables: {
      input: {
        scope: 'organization',
      },
    },
  });

  const {
    data: supplierData,
    error: supplierError,
    loading: supplierLoading,
  } = useQuery(SUPPLIER_QUERY, {
    variables: {
      id: supplierId,
    },
    onCompleted: (data) => {
      const supplierOrganizationUnitsActiveIds =
        data?.supplier.organizationUnitsActive.map((unit) => unit.id);

      if (supplierOrganizationUnitsActiveIds) {
        setSelectedOrganizationUnitsIds(supplierOrganizationUnitsActiveIds);
      }

      setSelectedTierOption({
        id: data?.supplier.tierForCurrentOrganization?.id || '',
      });
    },
  });

  const canUserSeeTiering = currentMembership.canShowTiering;
  const error = unitsError || supplierError;
  const loading = supplierLoading || unitsLoading;
  const organizationUnits = unitsData?.organizationUnits || [];
  const supplier = supplierData?.supplier;

  useEffect(() => {
    const removedUnits =
      supplier?.organizationUnitsActive.filter(
        ({ id }) => !selectedOrganizationUnitsIds.includes(id)
      ) || [];

    const removedUnitsWithActiveChildren = removedUnits?.filter(
      (unit) =>
        !!unit.activeStaffingRequestApplications.length ||
        !!unit.activeOffers.length ||
        !!unit.activeExtensionOffers.length
    );

    setShowActiveItemsStep(!!removedUnitsWithActiveChildren?.length);
    setRemovedOrganizationUnitsIds(removedUnits.map((unit) => unit.id));
  }, [selectedOrganizationUnitsIds, supplier?.organizationUnitsActive]);

  const isActiveItemsStepValid = useMemo(
    () =>
      answers &&
      [
        answers.rejectSubmissions,
        answers.retractOffers,
        answers.retractExtensionOffers,
      ].every(hasAnswer),
    [answers]
  );

  if (error) {
    return <ErrorHandler error={error} log="SupplierLocationsUpdateSteps" />;
  }

  function handleMutationError() {
    createMessage(
      'error',
      'Supplier’s locations were not updated at this time'
    );
  }

  async function onModalConfirm() {
    try {
      const response = await updateSupplierDepartmentsMutation({
        variables: {
          supplierId,
          input: {
            organizationUnitIds: selectedOrganizationUnitsIds,
            rejectSubmissions: answers?.rejectSubmissions,
            retractExtensionOffers: answers?.retractExtensionOffers,
            retractOffers: answers?.retractOffers,
            notes: notes,
            tierId: selectedTierOption.id,
            roleIds: selectedRoles as string[],
          },
        },
      });

      if (response?.data?.updateSupplierDepartments) {
        navigate(ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE);
        createMessage('success', "Supplier's locations successfully updated");
      }

      if (response?.errors) {
        handleMutationError();
      }
    } catch (e) {
      handleMutationError();
    }
  }

  const answeredWithYes = Object.values(answers).some(Boolean);
  const requiredNotes = answeredWithYes && !notes.length;

  return (
    <PageSteps
      confirmLoading={mutationLoading}
      steps={[
        {
          title: 'Units',
          page: (
            <UnitSelectionOnTransferTables
              loading={loading}
              organizationUnits={organizationUnits}
              onOrganizationUnitIdsChange={setSelectedOrganizationUnitsIds}
              selectedOrganizationUnitIds={selectedOrganizationUnitsIds}
            />
          ),
          forwardButtonDisabled:
            loading || !selectedOrganizationUnitsIds.length,
        },
        ...(pathname.includes('activation')
          ? [
              {
                title: 'Roles',
                page: (
                  <SuppliersManagementRolesAccessSelectionStep
                    supplierId={supplierId}
                    selectedRoles={selectedRoles}
                    onRoleChange={setSelectedRoles}
                  />
                ),
                forwardButtonDisabled: !selectedRoles.length,
              },
            ]
          : []),
        ...(canUserSeeTiering
          ? [
              {
                title: 'Tier',
                page: (
                  <TieringRadioWithOptions
                    selectedTier={selectedTierOption}
                    onTierChange={setSelectedTierOption}
                  />
                ),
                forwardButtonDisabled: !selectedTierOption.id,
              },
            ]
          : []),
        ...(showActiveItemsStep
          ? [
              {
                title: 'Active Items',
                page: (
                  <SuppliersManagementLocationsUpdateStepActiveItems
                    answers={answers}
                    onAnswer={setAnswers}
                  />
                ),
                forwardButtonDisabled: !isActiveItemsStepValid,
              },
            ]
          : []),
        {
          title: 'Summary',
          page: (
            <SuppliersManagementLocationsUpdateStepReview
              answers={answers}
              selectedTier={selectedTierOption}
              selectedRoles={selectedRoles}
              supplierName={supplier?.name}
              organizationUnits={organizationUnits}
              selectedOrganizationUnitsIds={selectedOrganizationUnitsIds}
              removedOrganizationUnitsIds={removedOrganizationUnitsIds}
              onNotesChange={setNotes}
              showActiveItemsStep={showActiveItemsStep}
              requiredNotes={requiredNotes}
            />
          ),
          forwardButtonDisabled: requiredNotes,
          confirmButtonText: 'Confirm',
        },
      ]}
      onConfirm={() => {
        Modal.confirm({
          title: 'Confirm the updates',
          content: 'The supplier users will receive a notification email.',
          onOk: onModalConfirm,
        });
      }}
    />
  );
}
